<template>
    <div class="flex justify-between items-center flex-wrap gap-2">
        <div v-if="postbackLog" class="flex items-center gap-4 flex-wrap">
            <h1 class="text-2xl">Postback Log</h1>
            <SelectButton
                v-model="logView"
                :options="logViewOptions"
                optionLabel="label"
                optionValue="value"
            />
            <DatePicker
                v-model="dateRange"
                selectionMode="range"
                showIcon
                :showOnFocus="true"
                class="w-auto"
            />
        </div>
        <div v-else>
            <h1 class="text-2xl">Showing {{ isSeo ? 'Sites' : 'Toplists' }} links</h1>
            <p class="text-gray-600 mt-2">To set postbacks, go under the postback column to get started</p>
        </div>
        <ToggleButton
            v-model="postbackLog"
            @update:modelValue="toggleView"
            onLabel="View Links"
            offLabel="View Postback Log"
            severity="secondary"
            outlined
        />
    </div>

    <!-- Conditional rendering based on toggle -->
    <div class="mt-4">
        <template v-if="postbackLog">
            <PostbackLog v-if="logView === 'log'" :dateRange="dateRange" />
            <ParamsBreakdown v-else-if="logView === 'params'" :dateRange="dateRange" />
            <DepositsByClickId v-else :dateRange="dateRange" />
        </template>
        <Links v-else />
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import SelectButton from 'primevue/selectbutton';
import DatePicker from 'primevue/datepicker';
import Links from './Links.vue';
import PostbackLog from './PostbackLog.vue';
import DepositsByClickId from './DepositsByClickId.vue';
import ParamsBreakdown from '../ParamsBreakdown.vue';
import PostbackService from '@/services/PostbackService';

const router = useRouter();
const route = useRoute();

const postbackLog = ref(route.query.showing === 'postbacklog');
const logView = ref(route.query.view === 'deposits' ? 'deposits'
                  : route.query.view === 'params'   ? 'params'
                  : 'log');
// Conditional visibility: publisher only sees the Params breakdown tab if
// their own postbacks have any params.param1 populated. Hidden by default.
const hasParams = ref(false);
onMounted(async () => {
    try {
        const res = await PostbackService.hasParamsPostbacks();
        hasParams.value = !!res.data?.has;
    } catch (err) {
        console.error('Error probing params availability:', err);
        hasParams.value = false;
    }
    if (logView.value === 'params' && !hasParams.value) {
        logView.value = 'log';
        router.replace({ query: { ...route.query, view: undefined } });
    }
});

const logViewOptions = computed(() => {
    const opts = [
        { label: 'Postback Log', value: 'log' },
        { label: 'Deposits by Click ID', value: 'deposits' }
    ];
    if (hasParams.value) opts.push({ label: 'Params breakdown', value: 'params' });
    return opts;
});

// Default: yesterday to today
const today = new Date();
const yesterday = new Date();
yesterday.setDate(today.getDate() - 1);
const dateRange = ref([yesterday, today]);

watch(() => route.query.showing, (newShowing) => {
    postbackLog.value = newShowing === 'postbacklog';
}, { immediate: true });

// Sync view changes to URL and expand date range when switching to deposits
watch(logView, (newView) => {
    const query = { ...route.query };
    if (newView === 'deposits') query.view = 'deposits';
    else if (newView === 'params') query.view = 'params';
    else delete query.view;
    router.replace({ query });

    if (newView === 'deposits') {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

        if (!dateRange.value || !dateRange.value[0] || dateRange.value[0] > thirtyDaysAgo) {
            dateRange.value = [thirtyDaysAgo, new Date()];
        }
    }
}, { immediate: true });

const toggleView = (value) => {
    const newView = value ? 'postbacklog' : 'links';
    router.push({ query: { ...route.query, showing: newView } });
};
</script>

<style scoped>

</style>
